import {useContext} from "react";
import {NotificationsContext} from "../ui-components/Notifications";
import {useIntl} from "react-intl";
import {getDateSlug} from "../utils/date";
import {stringToSlug} from "../utils/string";
import useCountryAxiosInstance from "./useCountryAxiosInstance";
import evaluateOutputCalculation from "../utils/evaluateOutputCalculation";
import prepareConfigurationStep from "../utils/prepareConfigurationStep";
import {
  fetchProductsById,
  getConfiguratorProductsToFetch, reduceProducts
} from "../ui-components/pages/Users/hooks/useFetchFixedProducts";
import explodeSurveyResponses from "../utils/explodeSurveyResponses";
import evaluateBlockDependencies from "../utils/evaluateBlockDependencies";
import _ from "lodash";


const useConfigurationExport = (downloading, setDownloading) => {
  // context
  const { push } = useContext(NotificationsContext);

  // hooks
  const intl = useIntl();
  const apiRequest = useCountryAxiosInstance();

  const handler = async (aps, survey, {configuration, responses}) => {

    if (downloading)
      return;

    setDownloading(true);
    try {
      // retrieve survey products (possibly needed in order to compute calculations)
      let products = {};
      const data = prepareConfigurationStep(configuration, undefined, survey.service_id);
      const productsToFetch = getConfiguratorProductsToFetch(data, {products});
      if (productsToFetch.length) {
        const missingProducts = await fetchProductsById(apiRequest, productsToFetch);
        products = {...products, ...reduceProducts(missingProducts)};
      }

      // explode recursive questions (if any) in order to access their values
      const responsesGroups = {};
      const {groups, containsRecursive} = explodeSurveyResponses(Object.values(responses), configuration, false, false);
      groups.forEach((g, idx) => {
        const key = !containsRecursive ? survey.id : `${survey.id}_unnested-${idx}`;
        responsesGroups[key] = g;
      })

      // utilities
      const buildOutput = (c) => ({calculation: c, output_currency: false, output_ceil: false});
      const buildApsArray = ({id, id_pa_item, name, calculation, detail_multipliers}, context) => {
        const main_multiplier = evaluateOutputCalculation(buildOutput(calculation), context);
        const extendedDM = detail_multipliers.map((dm) => ({
          ..._.omit(dm, "calculation"),
          multiplier: evaluateOutputCalculation(buildOutput(dm.calculation), context),
        }));
        return {id, id_pa_item, name, measures: [{main_multiplier}], detail_multipliers: extendedDM}
      };

      // build payload
      const payload = {
        survey_id: survey.id,
        aps: aps.reduce(
          (acc, curr) => {
            const groupAps = Object.values(responsesGroups)
              .filter((rg) => {
                const context = {responses: rg, configuration};
                return evaluateBlockDependencies(curr.dependencies, context, true);
              })
              .map((rg, idx, arr) => {
                const context = {responses: rg, products, configuration};
                const updatedAps = {...curr, ...(arr.length > 1 ? {name:  `${curr.name}.${idx}`} : {})};
                return buildApsArray(updatedAps, context);
              });
            return [...acc, ...groupAps];
          },
          []
        )
      };

      // retrieve blob file from backend
      const options = {responseType: "blob"};
      const response = await apiRequest.post("/price_analysis/export", payload, options);

      // download pdf blob to file
      const url = URL.createObjectURL(response);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${getDateSlug()}_${stringToSlug(survey.name ?? 'no-name')}.xpwe`;
      link.click();
      link.remove();
    }
    catch (e) {
      push({type: 'error', title: intl.formatMessage({id: 'server_error'})});
      throw e;
    }
    finally {
      setDownloading(false)
    }
  }

  return handler;
};

export default useConfigurationExport;